'use client';
import type { PortableTextBlock } from '@portabletext/types';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

interface TableOfContentsProps {
  content: PortableTextBlock[];
}

const generateId = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const TableOfContents = ({ content }: TableOfContentsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract H2 headings from the Portable Text content
  const headings = content
    .filter(
      (block) =>
        block._type === 'block' &&
        block.style === 'h2' &&
        block.children?.[0]?.text,
    )
    .map((block) => ({
      text: block.children[0].text,
      id: generateId(block.children[0].text),
    }));

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100; // Offset to account for fixed header
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  if (headings.length === 0) return null;

  const previewHeadings = headings.slice(0, 3);
  const remainingHeadings = headings.slice(3);
  const hasMoreHeadings = remainingHeadings.length > 0;

  return (
    <div>
      <h3 className="text-sm font-semibold mb-3">On this page</h3>
      <nav>
        <ul className="space-y-1">
          {/* Always show first 4 headings */}
          {previewHeadings.map((heading) => (
            <li key={heading.id}>
              <button
                onClick={() => scrollToHeading(heading.id)}
                className="text-[#9A9A9A] hover:text-primary transition-colors text-sm text-left"
              >
                {heading.text}
              </button>
            </li>
          ))}

          {/* Collapsible remaining headings */}
          {hasMoreHeadings && (
            <li>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <ul className="space-y-1 pt-1">
                  {remainingHeadings.map((heading) => (
                    <li key={heading.id}>
                      <button
                        onClick={() => scrollToHeading(heading.id)}
                        className="text-[#9A9A9A] hover:text-primary transition-colors text-sm text-left"
                      >
                        {heading.text}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Toggle arrow button */}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center justify-center w-full mt-1 py-1 text-[#9A9A9A] hover:text-primary transition-all duration-200 group"
                aria-label={isExpanded ? 'Show less' : 'Show more'}
              >
                <ChevronDown
                  className={`w-5 h-5 transition-transform duration-300 ease-in-out ${
                    isExpanded ? 'rotate-180' : 'rotate-0'
                  }`}
                />
              </button>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
};
